import { Check, Copy, RefreshCw, ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link } from "react-router";
import remarkGfm from "remark-gfm";

import { Button } from "@/components/ui/button";
import type { BubbleMessage } from "@/features/assistant/types";
import { cn } from "@/lib/utils";

import { BlockView } from "./blocks/BlockView";
import { isInternalHref, splitByBlockRefs } from "./markdown";

export type { BubbleMessage };

interface Props {
  message: BubbleMessage;
  streaming?: boolean;
  onFeedback?: (messageId: number, feedback: "up" | "down") => void;
  onRetry?: () => void;
}

const markdownComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) =>
    isInternalHref(href) ? (
      <Link to={href!} className="underline">
        {children}
      </Link>
    ) : (
      <span>{children}</span>
    ),
  // Imagem externa = GET sem clique: vetor de exfiltração por prompt injection.
  img: () => null,
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="overflow-x-auto">
      <table>{children}</table>
    </div>
  ),
};

export function MessageBubble({
  message,
  streaming = false,
  onFeedback,
  onRetry,
}: Props) {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === "user";
  const parts = splitByBlockRefs(message.content);
  const byId = new Map(message.blocks.map((b) => [b.id, b]));
  const referenced = new Set(
    parts.flatMap((p) => (typeof p === "string" ? [] : [p.blockId])),
  );
  const unreferenced = message.blocks.filter((b) => !referenced.has(b.id));

  const copy = async () => {
    await navigator.clipboard.writeText(
      message.content.replace(/\[\[block:[a-z0-9_]+\]\]/gi, "").trim(),
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className={cn("flex", isUser ? "justify-end" : "justify-start")}
      data-testid={`assistant-message-${message.role}`}
    >
      <div
        className={cn(
          "max-w-[92%] rounded-lg px-3 py-2 text-sm",
          isUser ? "bg-primary text-primary-foreground" : "bg-muted",
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="space-y-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-semibold [&_table]:text-xs [&_ul]:list-disc [&_ul]:pl-5">
            {parts.map((part, i) =>
              typeof part === "string" ? (
                <ReactMarkdown
                  key={i}
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                  skipHtml
                >
                  {part}
                </ReactMarkdown>
              ) : byId.has(part.blockId) ? (
                <BlockView key={part.blockId} block={byId.get(part.blockId)!} />
              ) : null,
            )}
            {unreferenced.map((b) => (
              <BlockView key={b.id} block={b} />
            ))}
            {streaming && message.toolLabel && (
              <p
                className="animate-pulse text-xs text-muted-foreground"
                data-testid="assistant-tool-label"
              >
                {message.toolLabel}…
              </p>
            )}
            {streaming && !message.toolLabel && message.content === "" && (
              <p className="animate-pulse text-xs text-muted-foreground">
                Pensando…
              </p>
            )}
            {message.error && (
              <div
                className="flex items-center gap-2 text-xs text-destructive"
                data-testid="assistant-message-error"
              >
                <span>{message.error}</span>
                {onRetry && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2"
                    onClick={onRetry}
                    data-testid="assistant-retry"
                  >
                    <RefreshCw className="mr-1 h-3 w-3" /> Tentar de novo
                  </Button>
                )}
              </div>
            )}
            {!streaming && message.id && !message.error && (
              <div className="flex items-center gap-1 pt-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={copy}
                  aria-label="Copiar"
                  data-testid="assistant-copy"
                >
                  {copied ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
                {onFeedback && (
                  <>
                    <Button
                      size="icon"
                      variant="ghost"
                      className={cn(
                        "h-6 w-6",
                        message.feedback === "up" && "text-green-600",
                      )}
                      onClick={() => onFeedback(message.id!, "up")}
                      aria-label="Resposta útil"
                      data-testid="assistant-feedback-up"
                    >
                      <ThumbsUp className="h-3 w-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className={cn(
                        "h-6 w-6",
                        message.feedback === "down" && "text-destructive",
                      )}
                      onClick={() => onFeedback(message.id!, "down")}
                      aria-label="Resposta ruim"
                      data-testid="assistant-feedback-down"
                    >
                      <ThumbsDown className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
